import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { FaFont, FaCopy, FaRedo } from "react-icons/fa";

export default function CaseConverter() {
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
   useTheme();

  const convertCase = (type) => {
    if (!inputText) return;

    switch (type) {
      case 'sentence':
        setOutputText(inputText.charAt(0).toUpperCase() + inputText.slice(1).toLowerCase());
        break;
      case 'upper':
        setOutputText(inputText.toUpperCase());
        break;
      case 'lower':
        setOutputText(inputText.toLowerCase());
        break;
      case 'title':
        setOutputText(inputText.replace(/\w\S*/g, (txt) => 
          txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
        ));
        break;
      case 'mixed':
        setOutputText(inputText.split('').map((char, i) => 
          i % 2 === 0 ? char.toLowerCase() : char.toUpperCase()
        ).join(''));
        break;
      case 'inverse':
        setOutputText(inputText.split('').map(char => 
          char === char.toUpperCase() ? char.toLowerCase() : char.toUpperCase()
        ).join(''));
        break;
      default:
        setOutputText(inputText);
    }
  };

  const copyToClipboard = () => {
    if (!outputText) return;
    navigator.clipboard.writeText(outputText);
  };

  const resetConverter = () => {
    setInputText("");
    setOutputText("");
  };

  return (
    <>
      <HeaderSection toolId="caseConverter" />
      <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
        <div className="bg-white dark:text-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2 flex items-center">
              <FaFont className="mr-3" />
              Case Converter
            </h1>
            <p className="opacity-90">
              Transform text between different case formats instantly
            </p>
          </div>

          {/* Main Content */}
          <div className="p-6">
            {/* Language/Locale Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Language / Locale
              </label>
              <select 
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus:ring-green-500 focus:border-green-500"
                defaultValue="en-US"
              >
                <option value="en-US">English (United States)</option>
                {/* Add more language options as needed */}
              </select>
            </div>

            {/* Input Text Area */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Enter your text...
              </label>
              <textarea
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus:ring-green-500 focus:border-green-500 h-32"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Type or paste your text here..."
              />
            </div>

            {/* Conversion Buttons */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
              <button
                onClick={() => convertCase('sentence')}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md flex items-center justify-center transition"
              >
                Sentence case
              </button>
              <button
                onClick={() => convertCase('upper')}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md flex items-center justify-center transition"
              >
                UPPER CASE
              </button>
              <button
                onClick={() => convertCase('lower')}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md flex items-center justify-center transition"
              >
                lower case
              </button>
              <button
                onClick={() => convertCase('title')}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md flex items-center justify-center transition"
              >
                Title Case
              </button>
              <button
                onClick={() => convertCase('mixed')}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md flex items-center justify-center transition"
              >
                Mixed Case
              </button>
              <button
                onClick={() => convertCase('inverse')}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md flex items-center justify-center transition"
              >
                INvErSe CASE
              </button>
            </div>

            {/* Output Text Area */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Converted Text
              </label>
              <textarea
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 focus:ring-green-500 focus:border-green-500 h-32"
                value={outputText}
                readOnly
                placeholder="Your converted text will appear here..."
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={resetConverter}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md flex items-center gap-2 transition"
              >
                <FaRedo /> Reset
              </button>
              <button
                onClick={copyToClipboard}
                disabled={!outputText}
                className={`px-4 py-2 rounded-md flex items-center gap-2 transition ${outputText ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-gray-200 dark:bg-gray-700 cursor-not-allowed'}`}
              >
                <FaCopy /> Copy
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-100 dark:text-white dark:bg-gray-800 p-4 text-center text-sm text-gray-600">
            <p>Quickly convert text between different case formats</p>
          </div>
        </div>
      </div>
      <BottomSection toolId="caseConverter" />
    </>
  );
}